import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { companiesKey } from '../../../solaris-lens-common-lib/src/constants/queryKeys/Companies';
import {
  searchCompanies,
  updateCompany,
  createCompany,
  deleteCompany,
  getCompany
} from '../../../solaris-lens-common-lib/src/services/companies';
import { RootState } from '../../../store';
import { SearchCompaniesResponse } from '../../../types/companies';
import {
  Company,
  companiesFormDataType
} from '@/containers/AdminToolContainers/CompaniesContainer/types';
import useShouldRefresh from '@/hooks/useShouldRefresh';
import { usersKey } from '@/solaris-lens-common-lib/src/constants/queryKeys/Users';
import { getUsersByCompany } from '@/solaris-lens-common-lib/src/services/users';
import { UserApiData } from '@/types/users';

export const useSearchCompanies = (
  param: string,
  { useQueryOptions = {} } = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery<SearchCompaniesResponse>({
    queryKey: companiesKey.search(token, param, 0),
    queryFn: () => {
      return searchCompanies(
        token,
        param,
        0
      ) as Promise<SearchCompaniesResponse>;
    },
    enabled: Boolean(token && param),
    ...useQueryOptions
  });
};

export const useGetCompaniesInitial = ({
  searchParam = '',
  useQueryOptions = {},
  start = 0
} = {}) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  const includePulseData = true;
  return useQuery<SearchCompaniesResponse>({
    queryKey: companiesKey.search(token, searchParam, start, includePulseData),
    queryFn: () => {
      return searchCompanies(
        token,
        searchParam,
        start,
        includePulseData
      ) as Promise<SearchCompaniesResponse>;
    },
    enabled: Boolean(token && searchParam),
    ...useQueryOptions
  });
};

export const useGetCompanyDetails = ({ companyId }: { companyId: string }) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery<Company>({
    queryKey: companiesKey.detail(token, companyId),
    queryFn: async () => {
      const response = await getCompany(token, companyId);
      if (response?.status?.code === 200 && response?.data) {
        return response.data as Company;
      }
      throw new Error(
        response?.status?.message || 'Failed to fetch company details'
      );
    },
    enabled: Boolean(token && companyId)
  });
};

export const useGetUsersByCompany = ({
  companyName = '',
  useQueryOptions = {}
}) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery<UserApiData[]>({
    queryKey: usersKey.lists(token, companyName),
    queryFn: async () => {
      const response = await getUsersByCompany(token, companyName);
      if (response?.status?.code === 200 && Array.isArray(response?.data)) {
        return response.data as UserApiData[];
      }
      throw new Error(
        response?.status?.message || 'Failed to fetch users by company'
      );
    },
    enabled: Boolean(token && companyName),
    ...useQueryOptions
  });
};

export const useCreateCompany = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyData: Company) => {
      return createCompany(token, companyData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companiesKey.all });
    },
    onError: error => {
      console.error('Error on creating company:', error);
    }
  });
};

export const useUpdateCompany = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      companyData
    }: {
      companyId: string;
      companyData: companiesFormDataType;
    }) => {
      return updateCompany(token, companyId, companyData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companiesKey.all });
    },
    onError: error => {
      console.error('Error on updating company:', error);
    }
  });
};

export const useDeleteCompany = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      return deleteCompany(token, name);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companiesKey.all });
    },
    onError: error => {
      console.error('Error on deleting company:', error);
    }
  });
};

export { useSearchCompanies as default };
